import type { ResourceType } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import {
  apps,
  sourceAccountId,
  dataClasses,
  environment,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  vpc,
  resourceId,
  cloudProvider,
  resourceGroupName,
  tagKeys,
  tagKeyValues,
} from '@/components/queryBuilder/properties';
import type { FilterProperty } from '@/types/advanceFilter';

export const allBackupPolicyProperties: FilterProperty[][] = [
  [resourceType, dataClasses, environment, apps],
  [
    cloudProvider,
    sourceAccountId,
    sourceRegion,
    vpc,
    subnets,
    resourceGroupName,
  ],
  [resourceName, resourceId],
  [tagKeys, tagKeyValues],
];

const awsPolicySupportedProperties: FilterProperty[] = [
  resourceType,
  dataClasses,
  environment,
  apps,
  sourceAccountId,
  sourceRegion,
  vpc,
  subnets,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
];

const gcpPolicySupportedProperties: FilterProperty[] = [
  resourceType,
  dataClasses,
  environment,
  apps,
  sourceAccountId,
  sourceRegion,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
];

const azurePolicySupportedProperties: FilterProperty[] = [
  resourceType,
  dataClasses,
  environment,
  apps,
  sourceAccountId,
  sourceRegion,
  resourceGroupName,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
];

const cloudPropsMap: Record<Provider, FilterProperty[]> = {
  [Provider.Aws]: awsPolicySupportedProperties,
  [Provider.Gcp]: gcpPolicySupportedProperties,
  [Provider.Azure]: azurePolicySupportedProperties,
  [Provider.MongoAtlas]: [],
  [Provider.ProviderUnspecified]: [],
};

export const useBackupPolicySupportedProperties = (
  supportedResourceTypes: ResourceType[],
  sourceProviders?: Provider[]
): FilterProperty[][] => {
  return useMemo(() => {
    if (!sourceProviders?.length) {
      return allBackupPolicyProperties;
    }

    return allBackupPolicyProperties.map((group) =>
      group.filter((prop) =>
        sourceProviders.some((cloud) => {
          if (prop.name === resourceType.name) {
            return supportedResourceTypes.length > 1;
          } else if (prop.name === cloudProvider.name) {
            return sourceProviders.length > 1;
          } else {
            return cloudPropsMap[cloud].includes(prop);
          }
        })
      )
    );
  }, [sourceProviders, supportedResourceTypes]);
};
